import { useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { fetchSustainTeamMembers, SustainTeamMemberFilters } from "../../../store/thunks/ticketingPortalThunk";
import { SustainTeamApiMember } from "../../../store/slices/ticketingPortalSlice";
import { formatUserName } from "../../../utils/emailTouserName";

type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

// ─── Avatar colour palette ────────────────────────────────────────────────────
const AVATAR_COLORS = [
  "#034895", "#7B5EA7", "#0A8484", "#2E7D32", "#C2185B",
  "#E65100", "#01579B", "#6A1B9A", "#0078A7", "#4A148C",
];

const getAvatarColor = (email: string): string => {
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name: string): string => {
  const parts = name.split(" ").filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.slice(0, 2) ?? "??").toUpperCase();
};

// ─── Mapped shape (matches screen's TeamMember interface) ─────────────────────
export interface SustainTeamMemberMapped {
  id: string;
  name: string;
  initials: string;
  email: string;
  role: "lead" | "member";
  managerEmail: string | null;
  teamId: number | null;
  channelsOnDuty: { nav: boolean; email: boolean; teams: boolean };
  channelsOffDuty: { nav: boolean; email: boolean; teams: boolean };
  scope: "assigned" | "all" | "custom";
  avatarColor: string;
  startHour: string;
  endHour: string;
  workingStatus: string;
  triggersOnDuty: { newTicket: boolean; assigned: boolean; unassigned: boolean; comment: boolean; agingTicket: boolean };
  triggersOffDuty: { newTicket: boolean; assigned: boolean; unassigned: boolean; comment: boolean; agingTicket: boolean };
  customUrgencies: string[];
  workingDays: {
    baseDays: DayKey[];
    overrides: Record<string, boolean>;
  };
}

// ─── Mapping helpers ──────────────────────────────────────────────────────────
const mapNotificationScope = (scope: string): "assigned" | "all" | "custom" => {
  switch ((scope ?? "").toUpperCase()) {
    case "ALL_TICKETS":   return "all";
    case "ASSIGNED_ONLY": return "assigned";
    default:              return "custom";
  }
};

const mapApiMember = (m: SustainTeamApiMember): SustainTeamMemberMapped => {
  const name = formatUserName(m.userEmail);
  return {
    id:          String(m.id),
    name,
    initials:    getInitials(name),
    email:       m.userEmail,
    role:        (m.role ?? "").toLowerCase() === "lead" ? "lead" : "member",
    managerEmail: m.managerEmail ?? null,
    teamId:      m.teamId ?? null,
    channelsOnDuty:  { nav: m.channelInAppOnDuty,  email: m.channelEmailOnDuty,  teams: m.channelTeamsOnDuty },
    channelsOffDuty: { nav: m.channelInAppOffDuty, email: m.channelEmailOffDuty, teams: m.channelTeamsOffDuty },
    scope:       mapNotificationScope(m.notificationScope),
    avatarColor: getAvatarColor(m.userEmail),
    startHour:   m.shiftStartCst?.slice(0, 5) ?? "08:00",
    endHour:     m.shiftEndCst?.slice(0, 5)   ?? "17:00",
    workingStatus: m.workingStatus ?? "Off Duty",
    triggersOnDuty: {
      newTicket:  m.triggerNewTicketOnDuty,
      assigned:   m.triggerAssignedToMeOnDuty,
      unassigned: m.triggerUnassignedFromMeOnDuty,
      comment:    m.triggerCommentAddedOnDuty,
      agingTicket: m.triggerAgingTicketOnDuty,
    },
    triggersOffDuty: {
      newTicket:  m.triggerNewTicketOffDuty,
      assigned:   m.triggerAssignedToMeOffDuty,
      unassigned: m.triggerUnassignedFromMeOffDuty,
      comment:    m.triggerCommentAddedOffDuty,
      agingTicket: m.triggerAgingTicketOffDuty,
    },
    customUrgencies: m.customUrgencies ?? [],
    workingDays: { baseDays: ["mon", "tue", "wed", "thu", "fri"], overrides: {} },
  };
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
const DEBOUNCE_MS = 400;

/**
 * Fetches sustain team members via the Redux thunk (uses the app's shared
 * `createApiClient()` with proper auth headers — same pattern as fetchMyTickets).
 * Raw API data is stored in Redux; this hook maps it to the screen-friendly shape.
 */
const useSustainTeamMembers = (search: string, filters?: SustainTeamMemberFilters) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    sustainTeamMembers,
    sustainTeamMembersLoading,
    sustainTeamMembersError,
    sustainTeamMembersTotalCount,
  } = useSelector((state: RootState) => state.ticketingPortal);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Serialize to a stable key so filter-array identity changes on every render don't retrigger fetches.
  const filtersKey = JSON.stringify(filters ?? {});
  const doFetch = useCallback(
    (query: string) => { dispatch(fetchSustainTeamMembers(query, filters)); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, filtersKey],
  );

  const members = useMemo(
    () => sustainTeamMembers.map(mapApiMember),
    [sustainTeamMembers],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Always fetch when search changes, including when clearing search
    // No delay for empty search or initial load; debounce typed queries
    debounceRef.current = setTimeout(() => doFetch(search), search ? DEBOUNCE_MS : 0);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, doFetch]);

  return {
    members,
    loading:    sustainTeamMembersLoading,
    error:      sustainTeamMembersError,
    totalCount: sustainTeamMembersTotalCount,
    refetch:    () => doFetch(search),
  };
};

export default useSustainTeamMembers;

